import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Mail, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { PRIVACY, TERMS, SUPPORT_EMAIL, COMPANY } from "../lib/legalContent";

const UPDATED = "June 2026";

const Section = ({ s }) => (
  <section className="flex flex-col gap-2.5" data-testid={`legal-section-${s.n}`}>
    <h2 className="font-display text-base md:text-lg text-amber-50 flex gap-3">
      <span className="gold-text tabular-nums shrink-0">{s.n}.</span>
      <span>{s.h}</span>
    </h2>
    <div className="pl-0 sm:pl-9 flex flex-col gap-2.5">
      {s.p?.map((t, i) => <p key={i} className="text-sm text-slate-300 leading-relaxed">{t}</p>)}
      {s.list && (
        <dl className="flex flex-col gap-2">
          {s.list.map(([term, body], i) => (
            <div key={i} className="text-sm leading-relaxed">
              <dt className="inline font-semibold text-amber-200/90">{term} </dt>
              <dd className="inline text-slate-300">{body}</dd>
            </div>
          ))}
        </dl>
      )}
      {s.bullets && (
        <ul className="flex flex-col gap-1.5">
          {s.bullets.map((b, i) => (
            <li key={i} className="flex gap-2.5 text-sm text-slate-300 leading-relaxed">
              <span className="text-amber-400/70 mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400/70 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  </section>
);

// One renderer for both documents. Public: app-store reviewers and signed-out visitors must be able to read them.
const LegalPage = ({ doc }) => {
  const { user } = useAuth();
  const other = doc.slug === "privacy" ? TERMS : PRIVACY;
  useEffect(() => { window.scrollTo(0, 0); }, [doc.slug]);
  return (
    <div className="min-h-screen px-5 sm:px-6 py-8 max-w-3xl mx-auto flex flex-col gap-7" data-testid={`legal-page-${doc.slug}`}>
      <Link to={user ? "/lobby" : "/"} className="btn-gold rounded-full pl-2 pr-3 py-1.5 text-xs font-display tracking-wider flex items-center gap-1.5 self-start btn-lift" data-testid="legal-back">
        <ArrowLeft className="w-4 h-4" /> {user ? "Lobby" : "Back"}
      </Link>

      <header className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          {doc.slug === "privacy" ? <ShieldCheck className="w-8 h-8 gold-text shrink-0" /> : <FileText className="w-8 h-8 gold-text shrink-0" />}
          <div>
            <h1 className="font-display text-2xl sm:text-3xl text-amber-50" data-testid="legal-title">{doc.title}</h1>
            <p className="text-sm text-slate-400">{doc.kicker}</p>
          </div>
        </div>
        <div className="gold-card rounded-xl px-4 py-3 text-sm text-slate-300 leading-relaxed">{doc.intro}</div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-amber-300/70">Last updated {UPDATED} · {COMPANY}</div>
      </header>

      <div className="flex flex-col gap-6">
        {doc.sections.map((s) => <Section key={s.n} s={s} />)}
      </div>

      <footer className="border-t border-amber-300/15 pt-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <a href={`mailto:${SUPPORT_EMAIL}`} className="flex items-center gap-2 text-sm text-amber-300 hover:text-amber-200" data-testid="legal-support-email">
          <Mail className="w-4 h-4" /> {SUPPORT_EMAIL}
        </a>
        <div className="flex gap-4">
          <Link to="/contact" className="text-sm text-slate-400 hover:text-amber-200 underline underline-offset-4" data-testid="legal-contact-link">Contact us</Link>
          <Link to={`/${other.slug}`} className="text-sm text-slate-400 hover:text-amber-200 underline underline-offset-4" data-testid={`legal-link-${other.slug}`}>
            Read the {other.title}
          </Link>
        </div>
      </footer>
    </div>
  );
};

export const PrivacyPage = () => <LegalPage doc={PRIVACY} />;
export const TermsPage = () => <LegalPage doc={TERMS} />;
export default LegalPage;
